import { useCallback, useEffect, useState } from "react";
import { useTheme, Grid, Chip, MenuItem, Menu, Badge, SvgIcon, Typography } from "@mui/material";
import { useIntl } from "react-intl";

import AddIcon from "@mui/icons-material/Add";

import { DisplayZoneValue } from "./types";
import useEditedStyles from "../../../../hooks/useEditedStyles";
import PZZoneView from "./PZZoneView";
import { behaviors } from "./SmartalarmBoard";
import { triggers } from "./SmartalarmBoard";

const SelectedZones = ({
  label,
  items,
  onChange,
  onAllChange,
  value,
  saved,
  disabled,
}: {
  label: string;
  items: DisplayZoneValue[];
  onChange: (v: boolean, index: number) => void;
  onAllChange?: (v: boolean[]) => void;
  value: boolean[] | null;
  saved: boolean[] | null;
  disabled?: boolean;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedIndexes, setSelectedIndexes] = useState<number[] | null>(null);
  const open = Boolean(anchorEl);

  const { formatMessage } = useIntl();
  const theme = useTheme();
  const editedStyle = useEditedStyles();

  useEffect(() => {
    if (!value) {
      setSelectedIndexes(null);
      return;
    }
    setSelectedIndexes(
      value
        .map((item, itemIndex) => ({ item, itemIndex }))
        .filter(({ itemIndex }) => value[itemIndex])
        .map(({ itemIndex }) => itemIndex)
    );
  }, [value, theme]);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleItem = useCallback(
    (index: number) => {
      if (!value) {
        return;
      }
      value[index] = !value[index];
      setSelectedIndexes(
        items
          .map((item, itemIndex) => ({ item, itemIndex }))
          .filter(({ itemIndex }) => value[itemIndex])
          .map(({ itemIndex }) => itemIndex)
      );
      onChange(value[index], index);
    },
    [value, items, onChange]
  );

  const selectAll = useCallback(() => {
    if (!value || !onAllChange) {
      return;
    }
    setSelectedIndexes(Array.from({ length: items.length }).map((_, i) => i));
    value.forEach((_, i) => (value[i] = true));
    onAllChange(value);
  }, [value, items, onAllChange]);

  if (!selectedIndexes) {
    return null;
  }

  const zoneObjects = items
    .map((zone, zoneIndex) => ({
      zone,
      zoneIndex,
    }))
    .filter(({ zoneIndex }) => selectedIndexes.includes(zoneIndex));

  const inputZoneObjects = zoneObjects.filter(({ zone }) => !zone.output).sort(({ zone: a }, { zone: b }) => b.behavior - a.behavior);
  const outputZoneObjects = zoneObjects
    .filter(({ zone }) => zone.output)
    .sort(({ zone: a }, { zone: b }) => {
      if (a.trigger && b.trigger) {
        return a.trigger.localeCompare(b.trigger);
      } else if (a.trigger) {
        return -1;
      }
      return 1;
    });

  return (
    <Grid container spacing={1} sx={{ ml: 1, p: 1 }}>
      <Grid item sx={{ mr: 1 }}>
        <Chip
          label={label}
          onClick={handleClick}
          deleteIcon={<AddIcon />}
          onDelete={handleClick}
          disabled={selectedIndexes.length === items.length || disabled}
          className={saved && saved.filter((value, index) => value && !selectedIndexes.includes(index)).length > 0 ? editedStyle.withHover : ""}
        />
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          {onAllChange && (
            <MenuItem
              key={`item-al`}
              sx={{ fontStyle: "italic" }}
              onClick={() => {
                handleClose();
                selectAll();
              }}
            >
              {formatMessage({ defaultMessage: "Add all" })}
            </MenuItem>
          )}
          {items &&
            selectedIndexes &&
            items
              .map((item, itemIndex) => ({ item, itemIndex }))
              .filter(({ itemIndex }) => !selectedIndexes.includes(itemIndex))
              .map(({ item, itemIndex }) => {
                const behavior = behaviors.find(({ value }) => parseInt(value, 2) === item.behavior);
                const trigger = triggers.find(({ value }) => value === item.trigger);
                const color = behavior?.color || trigger?.color || theme.palette.primary.main;
                const icon = behavior?.icon || trigger?.icon;
                return (
                  <MenuItem
                    key={`item-${item.uuid}`}
                    className={[!item.enabled ? "disabledrow" : "", saved && saved[itemIndex] ? editedStyle.withHover : ""].filter((i) => !!i).join(" ")}
                    onClick={() => {
                      handleClose();
                      toggleItem(itemIndex);
                    }}
                    sx={{ postition: "relative" }}
                  >
                    <Badge
                      badgeContent={`p${item.port}`}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      sx={[
                        {
                          mx: 1,
                          mb: 1,
                          "& .MuiBadge-badge": {
                            left: -8,
                            top: 4,
                            border: `2px solid ${theme.palette.background.paper}`,
                            padding: "0 4px",
                            backgroundColor: color,
                            color: theme.palette.getContrastText(color),
                          },
                        },
                      ]}
                    >
                      <SvgIcon sx={{ color: color }}>{icon}</SvgIcon>
                    </Badge>
                    {[item.deleted && `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} |`, item.code, item.name].filter((i) => !!i).join(" ")}
                    {item.board !== undefined && (
                      <Typography variant="caption" component={"span"} style={{ position: "absolute", right: 5, bottom: -2 }}>
                        {item.board === 0 ? "(master)" : `(slave${item.board})`}
                      </Typography>
                    )}
                  </MenuItem>
                );
              })}
        </Menu>
      </Grid>
      {inputZoneObjects
        .sort((a, b) => {
          const aBehavior = behaviors.find(({ value }) => parseInt(value, 2) === a.zone.behavior);
          const bBehavior = behaviors.find(({ value }) => parseInt(value, 2) === b.zone.behavior);

          if (!aBehavior || !bBehavior) {
            return 0;
          }

          return aBehavior.order - bBehavior.order;
        })
        .map(({ zone, zoneIndex }) => (
          <PZZoneView
            key={zone.uuid}
            zone={zone}
            onDelete={disabled ? undefined : () => toggleItem(zoneIndex)}
            className={[!zone.enabled ? "disabledrow" : "", !saved?.[zoneIndex] || zone.deleted ? editedStyle.background : ""].filter(i => !!i).join(" ")}
          />
        ))}
      {outputZoneObjects.map(({ zone, zoneIndex }) => (
        <PZZoneView
          key={zone.uuid}
          zone={zone}
          onDelete={disabled ? undefined : () => toggleItem(zoneIndex)}
          className={[!zone.enabled ? "disabledrow" : "", !saved?.[zoneIndex] || zone.deleted ? editedStyle.background : ""].filter(i => !!i).join(" ")}
        />
      ))}
    </Grid>
  );
};

export default SelectedZones;
